import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, put, del } from '@/api/client';
import { Toggle } from '@/components/atoms/Toggle';
import { Button } from '@/components/atoms/Button';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { queryKeys } from '@/api/queryKeys';
import { confirmModal } from '@/store/confirmStore';
import { toast } from '@/store/toastStore';
import { subscribePush, unsubscribePush, isPushSupported } from '@/lib/webPush';
import { comma } from '@/lib/format';

interface MeSummary {
  point_balance: number;
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme, logoutOnClose, setLogoutOnClose } = useUIStore();
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => get<Record<string, any>>('/notifications/settings'),
  });

  /* 탈퇴 안내용 — 현재 보유 포인트를 사전에 알려주기 위해 조회 */
  const { data: me } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => get<MeSummary>('/users/me'),
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Record<string, any>) => put('/notifications/settings', patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-settings'] }),
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => del('/users/me'),
    onSuccess: () => {
      toast.success('탈퇴가 완료되었어요.');
      logout();
    },
  });

  return (
    <div className="container-base py-10">
      <header className="mb-6">
        <h1 className="text-3xl font-extrabold">설정</h1>
        <p className="mt-2 text-sm text-[var(--color-text-sub)]">
          알림, 보안, 계정 관리 등 서비스 이용 옵션을 설정할 수 있어요.
        </p>
      </header>

      {/* 화면 */}
      <Section title="화면">
        <Row label="다크 모드">
          <Toggle checked={theme === 'dark'} onChange={toggleTheme} />
        </Row>
      </Section>

      {/* 보안 */}
      <Section title="보안">
        <Row label="브라우저 종료 시 로그아웃">
          <Toggle checked={logoutOnClose} onChange={setLogoutOnClose} />
        </Row>
        <p className="text-xs text-[var(--color-text-sub)]">
          공용 기기에서 사용할 때 켜두세요. 탭/브라우저를 닫으면 자동으로 로그아웃돼요.
        </p>
      </Section>

      {/* 알림 설정 */}
      <Section title="알림">
        <Row label="댓글 답글">
          <Toggle
            checked={settings?.comment_reply ?? true}
            onChange={(v) => updateMutation.mutate({ comment_reply: v })}
          />
        </Row>
        <Row label="게시글 추천">
          <Toggle
            checked={settings?.post_like ?? true}
            onChange={(v) => updateMutation.mutate({ post_like: v })}
          />
        </Row>
        <Row label="팔로우">
          <Toggle
            checked={settings?.follow ?? true}
            onChange={(v) => updateMutation.mutate({ follow: v })}
          />
        </Row>
        <Row label="포인트 적립">
          <Toggle
            checked={settings?.point ?? true}
            onChange={(v) => updateMutation.mutate({ point: v })}
          />
        </Row>
        <Row label="증시뉴스/공지">
          <Toggle
            checked={settings?.news ?? true}
            onChange={(v) => updateMutation.mutate({ news: v })}
          />
        </Row>
        <Row label="푸시 알림">
          <Toggle
            checked={settings?.push_enabled ?? false}
            onChange={async (v) => {
              if (!isPushSupported()) {
                toast.error('이 브라우저는 푸시 알림을 지원하지 않아요.');
                return;
              }
              if (v) {
                const ok = await subscribePush();
                if (!ok) return;
              } else {
                await unsubscribePush();
              }
              updateMutation.mutate({ push_enabled: v });
            }}
          />
        </Row>
      </Section>

      {/* 계정 */}
      <Section title="계정">
        <div className="space-y-2">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate('/me/edit')}
          >
            프로필 수정
          </Button>
          <Button
            variant="ghost"
            fullWidth
            onClick={async () => {
              const ok = await confirmModal({
                title: '로그아웃',
                message: '정말 로그아웃 하시겠어요?',
                confirmLabel: '로그아웃',
              });
              if (ok) logout();
            }}
          >
            로그아웃
          </Button>
          <Button
            variant="ghost"
            fullWidth
            className="!text-[var(--color-rise)]"
            onClick={async () => {
              const balance = me?.point_balance ?? 0;
              const pointMsg = balance > 0
                ? `\n\n⚠ 보유 포인트 ${comma(balance)}P 가 전부 소멸돼요.\n교환 또는 사용 후 진행해 주세요.`
                : '\n\n⚠ 보유 포인트가 0P이지만, 탈퇴 후에는 포인트를 복구할 수 없어요.';
              const ok = await confirmModal({
                title: '회원 탈퇴',
                message:
                  '정말 탈퇴하시겠어요?\n이 작업은 되돌릴 수 없고, 작성한 게시글·댓글·매매일지는 유지되지만 계정은 즉시 삭제돼요.' +
                  pointMsg,
                confirmLabel: '탈퇴',
                tone: 'danger',
              });
              if (ok) deleteAccountMutation.mutate();
            }}
          >
            회원 탈퇴
          </Button>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
      <h2 className="mb-4 text-sm font-semibold text-[var(--color-text-sub)]">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      {children}
    </div>
  );
}
